import { useState } from "react";
import { Plus, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { EventFlowsList } from "./EventFlowsList";
import { EventFlowModal } from "./EventFlowModal";

export function FlowsManager() {
  const [showFlowModal, setShowFlowModal] = useState(false);
  const [editingFlow, setEditingFlow] = useState<string | null>(null);

  return (
    <div className="space-y-4 sm:space-y-6">
      <TooltipProvider>
        <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg sm:text-xl">Event Flows</CardTitle>
              <CardDescription className="text-sm">
                Automated workflows triggered by booking status changes
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <Info className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-md p-4" side="left">
                  <div className="space-y-2">
                    <h4 className="font-semibold">Event Flow Configuration</h4>
                    <p className="text-sm">Event flows are the <strong>automation rules</strong> that connect status transitions to actions:</p>
                    <ul className="text-sm space-y-1 list-disc pl-4">
                      <li><strong>Trigger Mapping:</strong> Define which status transitions activate specific communication flows</li>
                      <li><strong>Communication Channels:</strong> Configure email, SMS, and webhook notifications</li>
                      <li><strong>Template Selection:</strong> Link flows to specific communication templates</li>
                      <li><strong>Conditions & Timing:</strong> Set delivery conditions, delays, and priority levels</li>
                      <li><strong>Organization Scope:</strong> Manage flows per organization or platform-wide</li>
                    </ul>
                    <p className="text-sm pt-2">Flows bridge the gap between status changes and automated communications, making your booking system intelligent and responsive.</p>
                  </div>
                </TooltipContent>
              </Tooltip>
              <Button 
                onClick={() => setShowFlowModal(true)}
                className="w-full sm:w-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Flow
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <EventFlowsList
            onEdit={(flowId) => {
              setEditingFlow(flowId);
              setShowFlowModal(true);
            }}
          />
        </CardContent>
        </Card>

        <EventFlowModal
          open={showFlowModal || !!editingFlow}
          onOpenChange={(open) => {
            if (!open) {
              setShowFlowModal(false);
              setEditingFlow(null);
            }
          }}
          flowId={editingFlow}
        />
      </TooltipProvider>
    </div>
  );
}